import { create } from "zustand";
import TeacherService from "../lib/teacher-service";
import type {
  TranscriptMessageValues,
  BuildQuestionValues,
} from "@live-writing/models";

interface PreLessonQuestionsStore {
  clarifyMessages: TranscriptMessageValues[];
  isSendingClarify: boolean;
  activeClarifyQuestion: BuildQuestionValues | null;
  teacherResponseId?: string | null;
  buildFromStudentSubmissions: (preLessonId: number) => Promise<void>;

  startClarify: (
    preLessonId: number,
    question: BuildQuestionValues,
  ) => Promise<void>;

  sendClarifyMessage: (preLessonId: number, message: string) => Promise<void>;

  exitClarify: () => void;
}

export const usePreLessonQuestionsStore = create<PreLessonQuestionsStore>()(
  (set, get) => ({
    clarifyMessages: [],
    isSendingClarify: false,
    activeClarifyQuestion: null,

    buildFromStudentSubmissions: async (preLessonId: number) => {
      try {
        await TeacherService.buildFromStudentSubmissions(preLessonId);
      } catch (err) {
        console.error("Failed to build questions:", err);
      }
    },

    startClarify: async (
      _preLessonId: number,
      question: BuildQuestionValues,
    ) => {
      set({
        activeClarifyQuestion: question,
        clarifyMessages: [],
        teacherResponseId: null,
      });
    },

    sendClarifyMessage: async (preLessonId: number, message: string) => {
      const { isSendingClarify, clarifyMessages } = get();
      if (isSendingClarify) return;

      set({ isSendingClarify: true });

      if (
        !clarifyMessages.some((m) => m.content === message && m.role === "user")
      ) {
        set((state) => ({
          clarifyMessages: [
            ...state.clarifyMessages,
            { role: "user", content: message },
          ],
        }));
      }

      try {
        const previousResponseId = get().teacherResponseId ?? undefined;
        const questionItem = get().activeClarifyQuestion ?? undefined;
        const { response_id } = await TeacherService.startChatClarifyJob(
          preLessonId,
          message,
          previousResponseId,
          questionItem,
        );

        set({ teacherResponseId: response_id || null });

        const poll = async () => {
          try {
            const result = await TeacherService.pollChatClarifyJob(response_id);

            if (result.status === "completed") {
              set((state) => ({
                clarifyMessages: [
                  ...state.clarifyMessages,
                  { role: "assistant", content: result.response ?? "" },
                ],
                isSendingClarify: false,
              }));
            } else if (result.status === "error") {
              set((state) => ({
                clarifyMessages: state.clarifyMessages.slice(0, -1),
                isSendingClarify: false,
              }));
              console.error("Clarify job returned error:", result.error);
            } else {
              setTimeout(poll, 1000);
            }
          } catch (err) {
            set((state) => ({
              clarifyMessages: state.clarifyMessages.slice(0, -1),
              isSendingClarify: false,
            }));
            console.error("Failed to poll clarify job:", err);
          }
        };

        setTimeout(poll, 1000);
      } catch (err) {
        set((state) => ({
          clarifyMessages: state.clarifyMessages.slice(0, -1),
          isSendingClarify: false,
        }));
        console.error("Clarify failed:", err);
      }
    },

    exitClarify: () => {
      set({
        activeClarifyQuestion: null,
        clarifyMessages: [],
        isSendingClarify: false,
        teacherResponseId: null,
      });
    },
  }),
);
